import { AddFolderPill } from "@/src/components/AddFolderPill";
import { AnimatedModal } from "@/src/components/AnimatedModal";
import { CloseIcon } from "@/src/components/CloseIcon";
import { toasts } from "@/src/components/toasts/toasts";
import {
  DataRoomFolder,
  DdqlQuery,
  useAddDdqlToDataRoomMutation,
  useMoveDataRoomFolderMutation,
} from "@/src/graphql/generated";
import useGqlClient from "@/src/hooks/useGqlClient";
import { useInvalidateQueryKeys } from "@/src/hooks/useInvalidateQueryKeys";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

export function LinkDataRoomFolderModal(props: {
  ddql: DdqlQuery["ddql"];
  open: boolean;
  onClose: () => void;
}) {
  const { open, onClose, ddql } = props;
  const client = useGqlClient();
  const queryClient = useQueryClient();
  const { invalidateDataRoomFolders } = useInvalidateQueryKeys();
  const moveFolder = useMoveDataRoomFolderMutation(client);
  const addDdqlToDataRoom = useAddDdqlToDataRoomMutation(client);

  const parentFolder = props.ddql.dataRoomFolder
    ? props.ddql.dataRoomFolder.parentFolders[1]
    : null;

  const { dealId } = useParams<{ dealId: string }>();

  function handleFolderChange(folder: DataRoomFolder) {
    // if a folder has been selected, we need to just move the DDQL's data room folder.
    // if there is no folder selected, we need to trigger the creation of a new folder for the ddql and and all sublists and questions.

    if (parentFolder && props.ddql.dataRoomFolder) {
      moveFolder.mutate(
        {
          input: {
            folderID: props.ddql.dataRoomFolder.id,
            targetParentFolderID: folder.id,
          },
        },
        {
          onSuccess: () => {
            toasts.success("Moved successfully");
            invalidateDataRoomFolders();
            queryClient.invalidateQueries({
              queryKey: [
                "Ddql",
                {
                  id: props.ddql.id,
                  dealId,
                },
              ],
            });
          },
          onError: () => {
            toasts.error("Failed to move");
          },
        },
      );
    } else {
      // create a new folder for the ddql and and all sublists and questions.
      addDdqlToDataRoom.mutate(
        {
          ddqlId: props.ddql.id,
          dataRoomFolderId: folder.id,
        },
        {
          onSuccess: () => {
            toasts.success(`Added to ${folder.name}`);
            invalidateDataRoomFolders();
            queryClient.invalidateQueries({
              queryKey: [
                "Ddql",
                {
                  id: props.ddql.id,
                  dealId,
                },
              ],
            });
          },
          onError: () => {
            toasts.error(`Failed to add to ${folder.name}`);
          },
        },
      );
    }
  }

  return (
    <AnimatedModal size="md" open={open} onClose={onClose}>
      <div>
        <div className="flex items-center justify-between">
          <p className="text-concrete-900 font-semibold text-md">
            Link list to a Data Room folder
          </p>
          <CloseIcon onClose={onClose} />
        </div>

        <div className="mt-2">
          <p className="text-sm font-semibold text-concrete-700">
            Select folder
          </p>
          <AddFolderPill
            onFolderSelected={(folder) => {
              handleFolderChange(folder);
            }}
            selectedFolder={parentFolder}
          />
        </div>

        <div className="p-2 border border-gray-200 rounded-md mt-3 space-y-2">
          <p className="text-xs text-gray-500 font-semibold">
            Any documents uploaded as answers to questions in this list will
            automatically be uploaded to the selected Data Room folder
          </p>
          <p className="text-xs text-gray-500 font-semibold">
            Note, each question will have its own corresponding folder in the
            Data Room
          </p>
        </div>
      </div>
    </AnimatedModal>
  );
}

function Badge(props: { text: string }) {
  return (
    <div className=" w-5 h-5 items-center justify-center bg-gray-300 flex rounded-full">
      <p className="text-gray-700 text-xs">{props.text}</p>
    </div>
  );
}
